import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getToken } from '../api';

const fmtHeight = (inches) => {
  if (!inches) return '—';
  return `${Math.floor(inches / 12)}'${inches % 12}"`;
};
const fmtAge = (min, max) => (min || max) ? `${min || 21} — ${max || 65}` : '21 — 65';
const fmtIncome = (v) => {
  if (!v || v === 0) return 'No minimum';
  if (v >= 500000) return '$500K+';
  if (v >= 200000) return '$200K+';
  if (v >= 100000) return '$100K+';
  if (v >= 50000) return '$50K+';
  return 'No minimum';
};
const EARNING_LABELS = { i_primary: 'I\'m primary', equal: 'Equal', partner_primary: 'Partner primary', no_preference: 'No preference' };
const INTENTION_LABELS = { marriage: 'Marriage', long_term: 'Long term', open: 'Open to either', short_term_to_ltr: 'Short term → LTR' };
const SMOKING_LABELS = { never: 'Never', occasional_ok: 'Occasionally OK', no_preference: 'No preference' };
const DRUGS_LABELS = { never: 'Never', occasional_ok: 'Occasionally OK', no_preference: 'No preference' };
const KIDS_WANT_LABELS = { must_want: 'Must want', must_not_want: 'Must not want', open: 'Open' };
const KIDS_HAS_LABELS = { no_kids_only: 'No kids only', has_kids_ok: 'Has kids OK', no_preference: 'No preference' };
const EXCLUSIVITY_LABELS = { monogamy_only: 'Monogamy only', monogamy_open_to_evolving: 'Open to evolving', non_monogamy_required: 'Non-mono required', open_to_discussing: 'Open to discuss' };
const GATEWAY_LABELS = { traditional: 'Private', alternative: 'Open', no_preference: 'No preference' };
const ENERGY_LABELS = { dominant: 'Dominant partner', submissive: 'Submissive partner', switch: 'Switch / fluid', equal: 'Equal energy', no_preference: 'No preference' };
const DRIVE_LABELS = { high: 'High drive', moderate: 'Moderate drive', variable: 'Flexible', no_preference: 'No preference' };
const KINK_LABELS = { high_ok: 'Adventurous OK', moderate_ok: 'Moderately open', light_only: 'Lighter', no_preference: 'No preference' };

const HEIGHT_OPTS = [];
for (let i = 56; i <= 84; i++) {
  HEIGHT_OPTS.push({ label: `${Math.floor(i/12)}'${i%12}"`, value: i });
}

function SectionHeader({ title, subtitle }) {
  return (
    <div style={{ padding: '28px 24px 12px' }}>
      <div style={{ fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--gold)', fontWeight: 400, marginBottom: 4 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 13, color: 'var(--muted)', fontStyle: 'italic' }}>{subtitle}</div>}
    </div>
  );
}

function PrefRow({ label, value, rowKey, expanded, onToggle, children }) {
  return (
    <div style={{ borderBottom: '0.5px solid var(--border)' }}>
      <div
        onClick={() => onToggle(rowKey)}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 24px', cursor: 'pointer' }}
      >
        <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 300 }}>{label}</span>
        <span style={{ fontSize: 12, color: expanded ? 'var(--gold)' : 'rgba(201,168,76,0.7)', fontWeight: 300, fontStyle: 'italic' }}>
          {value || '—'}
        </span>
      </div>
      {expanded && (
        <div style={{ padding: '4px 24px 20px', background: 'rgba(255,255,255,0.015)' }}>
          {children}
        </div>
      )}
    </div>
  );
}

function RadioOpts({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
      {options.map(opt => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          style={{
            background: value === opt.value ? 'rgba(201,168,76,0.07)' : 'transparent',
            border: `0.5px solid ${value === opt.value ? 'var(--gold)' : 'rgba(255,255,255,0.1)'}`,
            color: value === opt.value ? 'var(--gold)' : 'rgba(255,255,255,0.78)',
            padding: '11px 16px', textAlign: 'left', fontSize: 14, fontWeight: 300,
            cursor: 'pointer', transition: 'all 0.15s', fontFamily: 'var(--font-ui)',
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export default function Desires() {
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [toast, setToast] = useState(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    if (!getToken()) { navigate('/login'); return; }
    api.get('/profile').then(p => setPrefs(p)).catch(() => {});
  }, [navigate]);

  const save = useCallback((updates) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      await api.post('/profile/preferences', updates).catch(() => {});
      setToast(true);
      setTimeout(() => setToast(false), 1800);
    }, 400);
  }, []);

  const update = (key, value) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    save({ [key]: value });
  };

  const toggle = (key) => setExpanded(e => e === key ? null : key);

  if (!prefs) {
    return (
      <div style={{ height: '100vh', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span className="spinner" />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#000', paddingBottom: 100 }}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 60, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(201,168,76,0.15)', border: '0.5px solid rgba(201,168,76,0.4)',
          color: 'var(--gold)', fontSize: 11, letterSpacing: 2, padding: '8px 20px',
          zIndex: 200, textTransform: 'uppercase',
        }}>
          Saved
        </div>
      )}

      {/* Header */}
      <div style={{ padding: '52px 24px 4px' }}>
        <div style={{ fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 6 }}>My Desires</div>
        <div style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: 14, color: 'var(--muted)' }}>
          Adjust who we surface for you
        </div>
      </div>

      {/* DEALBREAKERS */}
      <SectionHeader title="Dealbreakers" subtitle="Matches must pass these" />

      <PrefRow label="Age range" value={fmtAge(prefs.pref_age_min, prefs.pref_age_max)} rowKey="age" expanded={expanded === 'age'} onToggle={toggle}>
        <div style={{ display: 'flex', gap: 12, marginTop: 8, alignItems: 'center' }}>
          <input type="number" min={18} max={99} value={prefs.pref_age_min || 21}
            onChange={e => update('pref_age_min', parseInt(e.target.value))}
            style={{ flex: 1, background: 'transparent', border: 'none', borderBottom: '0.5px solid rgba(255,255,255,0.2)', color: 'var(--text)', fontSize: 16, padding: '8px 0', outline: 'none', fontFamily: 'var(--font-ui)' }} />
          <span style={{ color: 'var(--very-muted)' }}>—</span>
          <input type="number" min={18} max={99} value={prefs.pref_age_max || 65}
            onChange={e => update('pref_age_max', parseInt(e.target.value))}
            style={{ flex: 1, background: 'transparent', border: 'none', borderBottom: '0.5px solid rgba(255,255,255,0.2)', color: 'var(--text)', fontSize: 16, padding: '8px 0', outline: 'none', fontFamily: 'var(--font-ui)' }} />
        </div>
      </PrefRow>

      <PrefRow label="Distance" value={prefs.pref_relocation_open ? 'Anywhere' : `${prefs.pref_distance_miles || 50} mi`} rowKey="dist" expanded={expanded === 'dist'} onToggle={toggle}>
        <div style={{ marginTop: 12 }}>
          <input type="range" min={5} max={200} value={prefs.pref_distance_miles || 50}
            onChange={e => update('pref_distance_miles', parseInt(e.target.value))}
            disabled={prefs.pref_relocation_open}
            style={{ width: '100%', accentColor: 'var(--gold)', marginBottom: 12 }} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!prefs.pref_relocation_open}
              onChange={e => update('pref_relocation_open', e.target.checked)}
              style={{ accentColor: 'var(--gold)', width: 16, height: 16 }} />
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>Open to relocating</span>
          </label>
        </div>
      </PrefRow>

      <PrefRow
        label="Relationship"
        value={INTENTION_LABELS[prefs.pref_relationship_intention] || '—'}
        rowKey="intention" expanded={expanded === 'intention'} onToggle={toggle}
      >
        <RadioOpts
          options={[
            {label:'Long term leading to marriage',value:'marriage'},
            {label:'Long term committed',value:'long_term'},
            {label:'Open to either',value:'open'},
            {label:'Short term, open to long term',value:'short_term_to_ltr'},
          ]}
          value={prefs.pref_relationship_intention}
          onChange={v => { update('pref_relationship_intention', v); setExpanded(null); }}
        />
      </PrefRow>

      <PrefRow
        label="Height range"
        value={prefs.pref_height_min_inches || prefs.pref_height_max_inches
          ? `${fmtHeight(prefs.pref_height_min_inches)} — ${fmtHeight(prefs.pref_height_max_inches)}`
          : 'No preference'}
        rowKey="height" expanded={expanded === 'height'} onToggle={toggle}
      >
        <div style={{ display: 'flex', gap: 12, marginTop: 12, alignItems: 'center' }}>
          <select value={prefs.pref_height_min_inches || ''}
            onChange={e => update('pref_height_min_inches', e.target.value ? parseInt(e.target.value) : null)}
            style={{ flex: 1, background: 'transparent', border: 'none', borderBottom: '0.5px solid rgba(255,255,255,0.2)', color: 'var(--text)', fontSize: 14, padding: '8px 0', outline: 'none', fontFamily: 'var(--font-ui)' }}>
            <option value="">Min</option>
            {HEIGHT_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <span style={{ color: 'var(--very-muted)' }}>—</span>
          <select value={prefs.pref_height_max_inches || ''}
            onChange={e => update('pref_height_max_inches', e.target.value ? parseInt(e.target.value) : null)}
            style={{ flex: 1, background: 'transparent', border: 'none', borderBottom: '0.5px solid rgba(255,255,255,0.2)', color: 'var(--text)', fontSize: 14, padding: '8px 0', outline: 'none', fontFamily: 'var(--font-ui)' }}>
            <option value="">Max</option>
            {HEIGHT_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </PrefRow>

      <PrefRow label="Income floor" value={fmtIncome(prefs.pref_income_min)} rowKey="income" expanded={expanded === 'income'} onToggle={toggle}>
        <RadioOpts
          options={[
            {label:'No minimum',value:0},
            {label:'$50K+',value:50000},
            {label:'$100K+',value:100000},
            {label:'$200K+',value:200000},
            {label:'$500K+',value:500000},
          ]}
          value={prefs.pref_income_min || 0}
          onChange={v => { update('pref_income_min', v); setExpanded(null); }}
        />
        <div style={{ fontSize: 11, color: 'var(--very-muted)', marginTop: 10, fontStyle: 'italic', lineHeight: 1.6 }}>
          Net worth is also considered — a retired person with assets passes this.
        </div>
      </PrefRow>

      <PrefRow label="Earning dynamic" value={EARNING_LABELS[prefs.pref_earning_dynamic] || '—'} rowKey="earning" expanded={expanded === 'earning'} onToggle={toggle}>
        <RadioOpts
          options={[
            {label:"I want to be the primary earner",value:'i_primary'},
            {label:"Equal earners",value:'equal'},
            {label:"I want my partner to be the primary earner",value:'partner_primary'},
            {label:"No preference",value:'no_preference'},
          ]}
          value={prefs.pref_earning_dynamic}
          onChange={v => { update('pref_earning_dynamic', v); setExpanded(null); }}
        />
      </PrefRow>

      <PrefRow label="Exclusivity" value={EXCLUSIVITY_LABELS[prefs.pref_exclusivity] || '—'} rowKey="exclusivity" expanded={expanded === 'exclusivity'} onToggle={toggle}>
        <RadioOpts
          options={[
            {label:'Monogamy only',value:'monogamy_only'},
            {label:'Open to evolving — start exclusive, open to discussion',value:'monogamy_open_to_evolving'},
            {label:'Non-monogamy required',value:'non_monogamy_required'},
            {label:'Open to discussing',value:'open_to_discussing'},
          ]}
          value={prefs.pref_exclusivity}
          onChange={v => { update('pref_exclusivity', v); setExpanded(null); }}
        />
      </PrefRow>

      {/* PREFERENCES */}
      <SectionHeader title="Preferences" subtitle="These shape who we surface first" />

      <PrefRow label="Smoking" value={SMOKING_LABELS[prefs.pref_smoking] || '—'} rowKey="smoking" expanded={expanded === 'smoking'} onToggle={toggle}>
        <RadioOpts
          options={[{label:'Never',value:'never'},{label:'Occasionally OK',value:'occasional_ok'},{label:'No preference',value:'no_preference'}]}
          value={prefs.pref_smoking}
          onChange={v => { update('pref_smoking', v); setExpanded(null); }}
        />
      </PrefRow>

      <PrefRow label="Drugs" value={DRUGS_LABELS[prefs.pref_drugs] || '—'} rowKey="drugs" expanded={expanded === 'drugs'} onToggle={toggle}>
        <RadioOpts
          options={[{label:'Never',value:'never'},{label:'Occasionally OK',value:'occasional_ok'},{label:'No preference',value:'no_preference'}]}
          value={prefs.pref_drugs}
          onChange={v => { update('pref_drugs', v); setExpanded(null); }}
        />
      </PrefRow>

      <PrefRow label="Wanting children" value={KIDS_WANT_LABELS[prefs.pref_wants_children] || '—'} rowKey="wants_kids" expanded={expanded === 'wants_kids'} onToggle={toggle}>
        <RadioOpts
          options={[{label:'Must want children',value:'must_want'},{label:'Must not want children',value:'must_not_want'},{label:'Open',value:'open'}]}
          value={prefs.pref_wants_children}
          onChange={v => { update('pref_wants_children', v); setExpanded(null); }}
        />
      </PrefRow>

      <PrefRow label="Has children" value={KIDS_HAS_LABELS[prefs.pref_has_children] || '—'} rowKey="has_kids" expanded={expanded === 'has_kids'} onToggle={toggle}>
        <RadioOpts
          options={[{label:'No kids only',value:'no_kids_only'},{label:'Has kids OK',value:'has_kids_ok'},{label:'No preference',value:'no_preference'}]}
          value={prefs.pref_has_children}
          onChange={v => { update('pref_has_children', v); setExpanded(null); }}
        />
      </PrefRow>

      {/* INTIMATE COMPATIBILITY */}
      <SectionHeader title="Intimate compatibility" subtitle="How you connect physically and emotionally" />

      <PrefRow label="Connection style" value={GATEWAY_LABELS[prefs.pref_intimacy_gateway] || '—'} rowKey="i_gateway" expanded={expanded === 'i_gateway'} onToggle={toggle}>
        <RadioOpts
          options={[
            {label:'Private — keep it sacred and personal',value:'traditional'},
            {label:'Open — compatibility here matters',value:'alternative'},
            {label:'No strong preference',value:'no_preference'},
          ]}
          value={prefs.pref_intimacy_gateway}
          onChange={v => { update('pref_intimacy_gateway', v); setExpanded(null); }}
        />
      </PrefRow>

      <PrefRow label="Energy dynamic" value={ENERGY_LABELS[prefs.pref_intimacy_energy] || '—'} rowKey="i_energy" expanded={expanded === 'i_energy'} onToggle={toggle}>
        <RadioOpts
          options={[
            {label:'A more dominant partner',value:'dominant'},
            {label:'A more submissive partner',value:'submissive'},
            {label:'A switch — fluid roles',value:'switch'},
            {label:'Equal energy',value:'equal'},
            {label:'No strong preference',value:'no_preference'},
          ]}
          value={prefs.pref_intimacy_energy}
          onChange={v => { update('pref_intimacy_energy', v); setExpanded(null); }}
        />
      </PrefRow>

      <PrefRow label="Drive level" value={DRIVE_LABELS[prefs.pref_intimacy_drive] || '—'} rowKey="i_drive" expanded={expanded === 'i_drive'} onToggle={toggle}>
        <RadioOpts
          options={[
            {label:'High drive — physical connection is central',value:'high'},
            {label:'Moderate drive — meaningful but not constant',value:'moderate'},
            {label:'Open to flexibility',value:'variable'},
            {label:'No strong preference',value:'no_preference'},
          ]}
          value={prefs.pref_intimacy_drive}
          onChange={v => { update('pref_intimacy_drive', v); setExpanded(null); }}
        />
      </PrefRow>

      <PrefRow label="Openness" value={KINK_LABELS[prefs.pref_intimacy_kink] || '—'} rowKey="i_kink" expanded={expanded === 'i_kink'} onToggle={toggle}>
        <RadioOpts
          options={[
            {label:'Adventurous and explorative',value:'high_ok'},
            {label:'Moderately open',value:'moderate_ok'},
            {label:'Lighter, more traditional',value:'light_only'},
            {label:'No strong preference',value:'no_preference'},
          ]}
          value={prefs.pref_intimacy_kink}
          onChange={v => { update('pref_intimacy_kink', v); setExpanded(null); }}
        />
      </PrefRow>

      {/* PAUSE */}
      <SectionHeader title="Pause" subtitle="" />
      <div style={{ padding: '4px 24px 20px', borderBottom: '0.5px solid var(--border)' }}>
        <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
          <div>
            <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 300 }}>Take a break this week</div>
            {prefs.matching_paused && (
              <div style={{ fontSize: 11, color: 'var(--very-muted)', marginTop: 3 }}>Resumes in 7 days</div>
            )}
          </div>
          <div
            onClick={() => update('matching_paused', !prefs.matching_paused)}
            style={{
              width: 44, height: 24, borderRadius: 12,
              background: prefs.matching_paused ? 'rgba(201,168,76,0.3)' : 'rgba(255,255,255,0.1)',
              border: `1px solid ${prefs.matching_paused ? 'var(--gold)' : 'rgba(255,255,255,0.15)'}`,
              position: 'relative', cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0,
            }}
          >
            <div style={{
              position: 'absolute', top: 2,
              left: prefs.matching_paused ? 22 : 2,
              width: 18, height: 18, borderRadius: '50%',
              background: prefs.matching_paused ? 'var(--gold)' : 'rgba(255,255,255,0.4)',
              transition: 'left 0.2s',
            }} />
          </div>
        </label>
      </div>
    </div>
  );
}
